/**
 * web/src/screens/Settings/helpers.ts — the constants and pure helpers the
 * Settings sections share (card MudceTexpbq1: moved verbatim out of
 * index.tsx when the screen split into section modules).
 */
import { WHATS_NEW, type WhatNewEntry } from '../../lib/whatsNew';

export const isDate = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);

/** Custom occasions are `custom-1`..`custom-N` — the DO renumbers on save. */
export const isCustomKey = (key: string) => /^custom(-\d+)?$/.test(key);
/** Mirrors MAX_CUSTOM_OCCASIONS in shared/types.ts — the DO rejects past it. */
export const MAX_CUSTOM = 5;
/** The four common lead times as stepper anchors (card zkDivBtVIpAP). */
export const PRESET_LEADS = [7, 14, 30, 60];
/** A date picker needs a year; 2000 is a leap year, so 29 Feb is pickable. */
export const DATE_YEAR = '2000';
/** Build stamp injected by vite define — see Settings → Build. */
export const BUILD_ID = typeof __BUILD_ID__ === 'string' && __BUILD_ID__ ? __BUILD_ID__ : 'dev';

/** Card QR9phRSRQHNx — the build reference is year.month.build, and the
 *  Build card carries what's new in this one (JitsLab's About pattern).
 *
 *  Exactly ONE entry, always (founder 2026-10-17: "What's new listed twice").
 *  The old filter matched the rolling entry AND the frozen BUILD_ID entry and
 *  rendered both, and because rolling renders its heading as BUILD_ID the two
 *  headings were identical — one empty, one not. Rolling's items are folded
 *  in front of the frozen ones instead, so a release that has not yet frozen
 *  its list still shows, and an empty pair renders nothing at all. */
export const WHATS_NEW_FOR_BUILD: WhatNewEntry[] = (() => {
  const rolling = WHATS_NEW.find(e => e.version === 'rolling');
  const frozen = WHATS_NEW.filter(e => e.version === BUILD_ID);
  const items = [...(rolling?.items ?? []), ...frozen.flatMap(e => e.items)];
  if (items.length === 0) {
    /* BUILD_ID is `yy.mm.<commit count>` (package.json build:prod), so it will
       almost never equal a frozen entry's version. When it does not, the card
       still has to say what is new — otherwise it silently disappears, which
       is exactly what Jonathan saw on 2026-10-28 ("about/build details used to
       list new features, now missing"). Fall back to the newest entry we do
       have, dated honestly. */
    return WHATS_NEW.length ? [WHATS_NEW[0]] : [];
  }
  return [{
    version: BUILD_ID,
    date: frozen[0]?.date ?? rolling?.date ?? '',
    items,
  }];
})();

/** The server's honest reasons, in the app's voice (card 3UwJvUtiirGN).
 *  Anything unrecognised still says something true rather than "error". */
const RECAP_REASONS: Record<string, string> = {
  toggle_off: 'Turn the recap on first.',
  not_due: 'Next recap due — one a week.',
  not_paired: 'A recap needs you both — pair up first.',
  no_email: 'Add an email to your account to get the recap.',
  mail_off: 'Email is not configured in this environment.',
  mail_failed: 'Email Could Not Be Sent. Nothing Was Marked as Sent, so It Will Try Again.',
  nothing_to_report: 'A quiet week — nothing to recap yet.',
};
export const recapReason = (reason: string): string => RECAP_REASONS[reason] ?? 'The recap could not be sent just now.';

/** Card si3nCYgCqe4g — the row says WHEN the next recap goes, not only that
 *  the toggle is on. `nextDueAt` comes from the server's own window clock, so
 *  the date here and the send that actually fires cannot drift apart. */
export const recapWhen = (r: { due: boolean; nextDueAt: number }): string => {
  if (r.due || !r.nextDueAt) return 'Due now — it goes out next time you open Pear.';
  const day = new Date(r.nextDueAt).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long',
  });
  return `Next one on ${day}.`;
};
